import { maxLength } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import { Box } from 'braid-design-system';
import React, {
  type ChangeEvent,
  type FocusEvent,
  forwardRef,
  type Ref,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { INPUT_MAX_LENGTH_DEFAULT } from 'src/app/shared/constants/formDataFields';
import { PAY_DISPLAYED_PRESSED } from 'src/app/shared/dataAnalytics/schema/constants';
import {
  FEATURE_FILTER_CONTENT,
  useFeatureToggleData,
} from 'src/app/shared/featureToggle';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import { isANZCountryCode } from 'src/app/utils/country';
import {
  type Validators,
  filterContent,
  validateField,
} from 'src/app/utils/validation';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { sendContentSafetyTrackingEvents } from 'src/app/v1/dataAnalytics/utils/sendContentSafetyTrackingEvents';
import { actionUpdateSalaryText } from 'src/app/v1/state/modules/formData/salaryText';
import { TextFieldWithCounter } from 'src/app/v1/views/shared/TextFieldWithCounter';
import type { AppState } from 'src/types/appState';

import translations from './.vocab';

export const PayDisplayText = forwardRef((_props, ref: Ref<any>) => {
  const { salaryText, jobLocationCountry, hirerNation } = useSelector(
    (state: AppState) => ({
      salaryText: state.formData.salaryText,
      jobLocationCountry: state.session.jobLocationCountry,
      hirerNation: state.session.hirerAccount.nation,
    }),
    shallowEqual,
  );

  const isANZ: boolean = isANZCountryCode(jobLocationCountry || hirerNation);

  const dispatch = useDispatch();
  const country = (jobLocationCountry ?? hirerNation ?? 'AU').toLowerCase();
  const isFilterContentEnabled = useFeatureToggleData(FEATURE_FILTER_CONTENT);

  const { t } = useTranslations(translations);
  const { getLanguage } = useConfig();

  const [inputValue, setInputValue] = useState<string>(salaryText);
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null,
  );

  useImperativeHandle(ref, () => ({
    handleValidation: (skipIfNull: boolean) =>
      handleInputValidation(inputValue, skipIfNull),
  }));

  // Salary text can be updated in redux from other components
  useEffect(() => {
    setInputValue(salaryText);
  }, [salaryText]);

  const handleInputValidation = (
    value: string,
    skipIfNull: boolean = false,
  ): string | null => {
    let validators: Validators = [maxLength];
    if (isFilterContentEnabled) validators = [...validators, filterContent];

    const validationResult = validateField(
      {
        field: 'payDisplayText',
        validators,
        validatorProps: {
          max: 50,
          country,
          sendTrackingEvents: sendContentSafetyTrackingEvents,
        },
        value,
      },
      skipIfNull,
      getLanguage(),
    );
    setValidationMessage(validationResult);
    return validationResult;
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setInputValue(value);
    handleInputValidation(value);
  };

  const handleOnBlur = (event: FocusEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const message = handleInputValidation(value);
    if (!message) {
      dispatch(actionUpdateSalaryText(value));
    }

    const eventData = {
      eventName: PAY_DISPLAYED_PRESSED,
      objectInteraction: 'pay-details',
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  };

  // TODO: Uncomment after NVL switch
  // const handleTooltipToggleOn = () => {
  //   const eventData = {
  //     eventName: TOOLTIP_PRESSED,
  //     objectInteraction: 'salary-display-help',
  //     tooltipType: 'payDisplayed',
  //   };
  //   getTrackingAdapter.sendLinkEvent(eventData);
  // };

  return isANZ ? (
    <Box position="relative">
      <TextFieldWithCounter
        id="PayDetailsTextField"
        value={inputValue}
        onBlur={handleOnBlur}
        onChange={handleChange}
        isValid={!Boolean(validationMessage)}
        message={validationMessage || t('pay-display-text-default-message')}
        maxCharacters={INPUT_MAX_LENGTH_DEFAULT}
        data={{ 'hj-whitelist': 'true' }}
      />
      {/* TODO: (NVL) uncomment after theme switch */}
      {/* <Box right={0} top={0} position="absolute">
        <ContextualHelp onToggleOn={handleTooltipToggleOn}>
          <Text>{t('pay-display-text-help-text')}</Text>
        </ContextualHelp>
      </Box> */}
    </Box>
  ) : null;
});
